import React, { useEffect, useMemo, useState } from "react";
import { api, type ArtistDTO } from "../lib/api";
import type { Rating, SetPreferenceMap } from "../lib/preferences";
import { shareForArtist } from "../lib/share";
import { dayLabels } from "../lib/format";
import { ShareButton } from "./ShareButton";
import { IconBookmark, IconBookmarkFilled, IconHeart, IconMeh, IconPlay, IconStar, IconX } from "./icons";
import {
  addSetToSchedule,
  listSchedulesForEvent,
  removeSetFromSchedule,
  saveSchedule,
  schedulesContainingSet,
  type Schedule as SavedSchedule,
} from "../lib/schedules";
import type { EventPrefs } from "../lib/preferences";

type PlayerSource = "youtube" | "deezer" | "soundcloud" | "off";

export function ArtistSheet({
  eventSlug,
  eventStartDate,
  slug,
  rating,
  setRatings = {},
  onClose,
  onRate,
  onRateSet,
}: {
  eventSlug: string;
  /** ISO startDate of the parent event — used to label set days with the
   *  correct real-world dates (not EDC's hardcoded May 15-17). */
  eventStartDate?: string | null;
  slug: string;
  rating: Rating | null;
  /** Map of per-set overrides for this event — used to show per-set hearts. */
  setRatings?: SetPreferenceMap;
  onClose: () => void;
  onRate: (slug: string, rating: Rating | null) => void;
  /** Omit to disable per-set controls (falls back to artist-only). */
  onRateSet?: (setId: number, rating: Rating | null) => void;
}) {
  const [artist, setArtist] = useState<ArtistDTO | null>(null);
  const [player, setPlayer] = useState<PlayerSource>("youtube");

  // Per-event day labels. Derive for up to 14 days (matches the `sets.day`
  // CHECK constraint) so any festival length resolves correctly.
  const DAY_LABELS = useMemo(
    () => dayLabels(eventStartDate ?? "2026-01-01", 14, { dot: false }),
    [eventStartDate],
  );

  useEffect(() => {
    let cancelled = false;
    api.artist(eventSlug, slug).then(a => {
      if (cancelled) return;
      setArtist(a);
      if (a.youtubeVideo) setPlayer("youtube");
      else if (a.deezerId) setPlayer("deezer");
      else setPlayer("soundcloud");
    });
    return () => {
      cancelled = true;
    };
  }, [eventSlug, slug]);

  const initial =
    artist?.name.replace(/[^A-Za-z0-9]/g, "").charAt(0).toUpperCase() || "?";

  return (
    <div
      className="sheet-backdrop"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      data-testid="artist-sheet"
    >
      <div className="sheet">
        <div className="sheet-handle" />
        <div className="sheet-body">
          {artist ? (
            <>
              {artist.photoUrl ? (
                <div
                  className="sheet-hero"
                  style={{ backgroundImage: `url(${artist.photoUrl})` }}
                >
                  <button className="close-x" onClick={onClose} aria-label="Close" data-testid="close-sheet">
                    <IconX />
                  </button>
                </div>
              ) : (
                <div className="sheet-hero no-photo">
                  {initial}
                  <button className="close-x" onClick={onClose} aria-label="Close" data-testid="close-sheet">
                    <IconX />
                  </button>
                </div>
              )}

              <span className="sub">{artist.genre?.toUpperCase() ?? "ARTIST"}</span>
              <h1 data-testid="sheet-name">{artist.name}</h1>

              <div className="meta">
                {artist.sets.map(s => (
                  <span key={s.id} className="day-pill">
                    D{s.day}{s.startLabel ? ` · ${s.startLabel}` : ""} · {s.stage.name}
                  </span>
                ))}
              </div>

              {artist.bio && <p style={{ color: "var(--ink-dim)", lineHeight: 1.5 }}>{artist.bio}</p>}

              <div className="set-list">
                {artist.sets.map(s => {
                  const override = setRatings[s.id] ?? null;
                  // Effective rating (what the schedule will use).
                  const effective = override ?? rating ?? null;
                  return (
                    <div key={s.id} className="set-row" data-testid={`set-row-${s.id}`}>
                      <div className="col" style={{ minWidth: 0, flex: 1 }}>
                        <span className="sub">DAY {s.day} · {DAY_LABELS[s.day - 1]}</span>
                        <span className="stage-name">{s.stage.name}</span>
                        {s.startLabel && (
                          <span className="lcd-time" style={{ alignSelf: "flex-start", marginTop: 4 }}>
                            {s.startLabel} → {s.endLabel}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                          alignItems: "flex-end",
                          flexShrink: 0,
                        }}
                      >
                        {onRateSet && artist.sets.length > 1 && (
                          <SetRatingRow
                            setId={s.id}
                            override={override}
                            effective={effective}
                            onRateSet={onRateSet}
                          />
                        )}
                        <AddToScheduleButton eventSlug={eventSlug} setId={s.id} />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="rate-row" role="group" aria-label="Rate this artist">
                {(["pass", "meh", "like", "superlike"] as Rating[]).map(r => (
                  <button
                    key={r}
                    className={`rate-btn ${r} ${rating === r ? "active" : ""}`}
                    onClick={() => onRate(artist.slug, r)}
                    data-testid={`rate-${r}`}
                  >
                    {r === "superlike" ? "SUPER" : r.toUpperCase()}
                  </button>
                ))}
              </div>

              {/* Share artist */}
              <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
                <ShareButton
                  label="Share artist"
                  data-testid="share-artist"
                  content={() => {
                    const event = {
                      slug: artist.eventSlug,
                      name: artist.eventSlug.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
                    } as import("../lib/api").EventDTO;
                    return shareForArtist(artist, event);
                  }}
                />
              </div>

              <div className="player" data-testid="player">
                <div className="player-tabs">
                  {artist.youtubeVideo && (
                    <button
                      className="chip"
                      aria-pressed={player === "youtube"}
                      onClick={() => setPlayer("youtube")}
                      data-testid="player-youtube"
                    >
                      <IconPlay /> DJ set
                    </button>
                  )}
                  {artist.deezerId && (
                    <button
                      className="chip"
                      aria-pressed={player === "deezer"}
                      onClick={() => setPlayer("deezer")}
                      data-testid="player-deezer"
                    >
                      <IconPlay /> Deezer
                    </button>
                  )}
                  <button
                    className="chip"
                    aria-pressed={player === "soundcloud"}
                    onClick={() => setPlayer("soundcloud")}
                    data-testid="player-soundcloud"
                  >
                    <IconPlay /> SoundCloud
                  </button>
                  <button
                    className="chip"
                    aria-pressed={player === "off"}
                    onClick={() => setPlayer("off")}
                  >
                    Off
                  </button>
                </div>

                {player === "youtube" && artist.youtubeVideo && (
                  <>
                    <iframe
                      title={`${artist.name} DJ set on YouTube`}
                      src={`https://www.youtube-nocookie.com/embed/${artist.youtubeVideo.id}?modestbranding=1&rel=0`}
                      height={200}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                    {artist.youtubeVideo.title && (
                      <div
                        style={{
                          fontFamily: "var(--fs-mono)",
                          fontSize: 10,
                          letterSpacing: "0.08em",
                          color: "var(--ink-dim)",
                          marginTop: 6,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={artist.youtubeVideo.title}
                      >
                        ▶ {artist.youtubeVideo.title}
                      </div>
                    )}
                  </>
                )}
                {player === "deezer" && artist.deezerId && (
                  <iframe
                    title={`${artist.name} on Deezer`}
                    src={`https://widget.deezer.com/widget/dark/artist/${artist.deezerId}?tracklist=true`}
                    height={300}
                    allow="encrypted-media; clipboard-write"
                    allowTransparency
                  />
                )}
                {player === "soundcloud" && (
                  <iframe
                    title={`${artist.name} on SoundCloud`}
                    src={
                      artist.links.soundcloudVerified && artist.links.soundcloud
                        ? `https://w.soundcloud.com/player/?url=${encodeURIComponent(artist.links.soundcloud)}&color=%23ff6a13&visual=true&show_artwork=true&show_user=true`
                        : `https://w.soundcloud.com/player/?url=${encodeURIComponent(
                            `https://soundcloud.com/search?q=${artist.name}`,
                          )}&color=%23ff6a13&visual=true&show_artwork=true`
                    }
                    height={300}
                    allow="autoplay; encrypted-media"
                  />
                )}
              </div>

              <div className="links">
                {artist.links.instagramVerified && artist.links.instagram && (
                  <a
                    className="link-btn"
                    href={artist.links.instagram}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="link-instagram"
                  >
                    Instagram ✓
                  </a>
                )}
                {artist.youtubeVideo && (
                  <a
                    className="link-btn"
                    href={`https://www.youtube.com/watch?v=${artist.youtubeVideo.id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    YouTube ↗
                  </a>
                )}
                {artist.deezerLink && (
                  <a className="link-btn" href={artist.deezerLink} target="_blank" rel="noreferrer">
                    Deezer
                  </a>
                )}
                {artist.links.soundcloudVerified && artist.links.soundcloud && (
                  <a className="link-btn" href={artist.links.soundcloud} target="_blank" rel="noreferrer">
                    SoundCloud ✓
                  </a>
                )}
                {artist.links.website && (
                  <a className="link-btn" href={artist.links.website} target="_blank" rel="noreferrer">
                    Website
                  </a>
                )}
              </div>

              {player === "youtube" && artist.youtubeVideo && (
                <div
                  style={{
                    marginTop: 10, fontSize: 11, color: "var(--ink-faint)",
                    textAlign: "center", lineHeight: 1.5,
                  }}
                >
                  If the player says "video unavailable", the uploader disabled embedding —{" "}
                  <a
                    href={`https://www.youtube.com/watch?v=${artist.youtubeVideo.id}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "var(--amber-hot)" }}
                  >
                    watch on YouTube ↗
                  </a>
                </div>
              )}
            </>
          ) : (
            <div className="empty">Loading…</div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Compact per-set rating control — four small beveled buttons (PASS / MEH /
 * LIKE / SUPER) shown on each set row when an artist has more than one set
 * at the event. Tapping the currently-active override clears it, letting
 * the set fall back to the artist-level rating.
 */
function SetRatingRow({
  setId,
  override,
  effective,
  onRateSet,
}: {
  setId: number;
  override: Rating | null;
  effective: Rating | null;
  onRateSet: (setId: number, rating: Rating | null) => void;
}) {
  const items: Array<{ r: Rating; icon: JSX.Element; label: string }> = [
    { r: "meh", icon: <IconMeh />, label: "Meh" },
    { r: "like", icon: <IconHeart />, label: "Like" },
    { r: "superlike", icon: <IconStar />, label: "Super" },
  ];
  return (
    <div className="set-rate" role="group" aria-label="Rate this set">
      {items.map(({ r, icon, label }) => {
        const isActive = override === r;
        const isFallback = override === null && effective === r;
        return (
          <button
            key={r}
            type="button"
            className={`set-rate-btn ${r} ${isActive ? "active" : ""} ${isFallback ? "fallback" : ""}`}
            onClick={() => onRateSet(setId, isActive ? null : r)}
            aria-pressed={isActive}
            aria-label={`${label} this set`}
            data-testid={`set-rate-${setId}-${r}`}
            title={
              isFallback
                ? `Inherited from artist (tap to override)`
                : isActive
                ? `Remove override (falls back to artist)`
                : label
            }
          >
            {icon}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Per-set "Add to…" picker — curator-mode action that lets you drop this
 * specific set into one of your saved named schedules WITHOUT touching
 * your own likes. Tapping the bookmark opens a small popover listing all
 * saved schedules for the event plus a "New…" row; tapping a row toggles
 * the set in/out of that schedule.
 */
function AddToScheduleButton({
  eventSlug,
  setId,
}: {
  eventSlug: string;
  setId: number;
}) {
  const [open, setOpen] = useState(false);
  const [schedules, setSchedules] = useState<SavedSchedule[]>(() => listSchedulesForEvent(eventSlug));
  const containingIds = useMemo(() => {
    const list = schedulesContainingSet(eventSlug, setId);
    return new Set(list.map(s => s.id));
  }, [eventSlug, setId, schedules]);

  useEffect(() => {
    const refresh = () => setSchedules(listSchedulesForEvent(eventSlug));
    window.addEventListener("schedules:changed", refresh);
    return () => window.removeEventListener("schedules:changed", refresh);
  }, [eventSlug]);

  const anyContains = containingIds.size > 0;

  const toggle = (scheduleId: string) => {
    if (containingIds.has(scheduleId)) removeSetFromSchedule(scheduleId, setId);
    else addSetToSchedule(scheduleId, setId, "like");
  };

  const createAndAdd = () => {
    const name = window.prompt("New schedule name:");
    if (!name) return;
    const s = saveSchedule({
      name,
      eventSlug,
      picks: { artists: {}, sets: { [setId]: "like" } },
    });
    if (s) setOpen(false);
  };

  return (
    <div className="add-to-schedule" data-testid={`add-to-schedule-${setId}`}>
      <button
        type="button"
        className={`add-to-schedule-btn ${anyContains ? "active" : ""}`}
        aria-label={anyContains ? "In curated schedules" : "Add to schedule"}
        aria-pressed={anyContains}
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
        title={
          anyContains
            ? `In ${containingIds.size} schedule${containingIds.size === 1 ? "" : "s"}`
            : "Add to a curated schedule"
        }
      >
        {anyContains ? <IconBookmarkFilled /> : <IconBookmark />}
      </button>
      {open && (
        <>
          <div
            className="add-to-schedule-scrim"
            aria-hidden="true"
            onClick={() => setOpen(false)}
          />
          <div className="add-to-schedule-menu" role="menu">
            <div className="add-to-schedule-head">
              <span className="super">ADD TO SCHEDULE</span>
            </div>
            {schedules.length === 0 && (
              <div className="add-to-schedule-empty">
                No saved schedules yet.
              </div>
            )}
            {schedules.map(s => {
              const has = containingIds.has(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={has}
                  className={`add-to-schedule-row ${has ? "on" : ""}`}
                  onClick={() => toggle(s.id)}
                  data-testid={`add-to-${s.id}`}
                >
                  <span className="add-to-schedule-check">{has ? "✓" : ""}</span>
                  <span className="add-to-schedule-name">{s.name}</span>
                </button>
              );
            })}
            <button
              type="button"
              className="add-to-schedule-row new"
              onClick={createAndAdd}
              data-testid="add-to-new"
            >
              <span className="add-to-schedule-check">+</span>
              <span className="add-to-schedule-name">New schedule…</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
